import { useCallback, useEffect } from "react";
import { useSetAtom } from "jotai";
import { useLocationApi } from "@/hooks/apiHooks/useLocationApi";
import { startShipToLocationsLoadAtom } from "@/store/shipToAtoms";

// Loads the switcher's location list once.
//
// The read is CLAIMED rather than guarded by a flag (see startShipToLocationsLoadAtom): both
// navs mount a switcher, so an unclaimed read is issued twice. `retry` is the same path — a
// failed read is claimable, so the shopper can ask again without a page reload.
//
// No status of its own. getLocations settles the shared location atoms on BOTH branches and
// shipToLocationsStatusAtom derives the switcher's four cells from them, so a list loaded by any
// other surface — Checkout, the confirmation, either account page — settles the header too. The
// status this hook used to own could only ever be settled by this read, which left the header
// showing its error for the rest of the session after somebody else's read had succeeded.
export const useShipToLocations = (): { retry: () => void } => {
    const { getLocations } = useLocationApi();
    const startLoad = useSetAtom(startShipToLocationsLoadAtom);

    const load = useCallback(() => {
        if (!startLoad()) {
            return;
        }

        getLocations().catch(() => {
            // getLocations already raised the toast and set locationsFetchErrorAtom, which is
            // what the switcher renders its failed cell from.
        });
    }, [startLoad, getLocations]);

    useEffect(() => {
        load();
    }, [load]);

    return { retry: load };
};
